import classNames from 'classnames';
import { Field, useFormikContext } from 'formik';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import callApi from '../../../../utils/callApi';
import { LOADING_TYPES } from '../../../../utils/checkoutState';
import { config } from '../../../../utils/config';
import { useFormName } from '../../../../utils/formName';
import getValidator from '../../../../utils/getValidator';
import { useNotifications } from '../../../../utils/notifications';
import {
  pushErrorTrackingEventFromErrorObject,
  pushErrorTrackingEventFromI18nKey,
  pushTrackingEvent
} from '../../../../utils/tracking';
import useLocalizedDateFormat from '../../../../utils/useLocalizedDateFormat';
import Button from '../../../atoms/Button';
import getBirthDateValidator from '../../../atoms/DateInput/getBirthDateValidator';
import InformationBox from '../../../atoms/InformationBox';
import Modal from '../../../atoms/Modal';
import EmailField from '../../../fields/EmailField';
import PasswordField from '../../../fields/PasswordField';
import DateField from '../../../formik/DateField';
import TextField from '../../../formik/TextField';
import { EmbeddedAuthBaseProps } from '../../../organisms/EmbeddedAuth';
import { showDefaultError, showNotification } from '../../Notifications';
import ThirdPartySignIn from '../ThirdPartySignIn';
import ForgotPassword from './ForgotPassword';
import './style.scss';

const SignIn = ({ isEmbeddedAuth, isWelcomeBack }: EmbeddedAuthBaseProps & { isWelcomeBack?: boolean }) => {
  const { submitForm, isSubmitting, setSubmitting, values, setFieldValue } = useFormikContext<{
    email: string;
    publicKey: string;
    birthDate: string;
    customerNumberEmail: string;
    emailHint: string;
    signInWithCustomerNumber: 'inactive' | 'active' | 'verified' | 'setPassword';
  }>();
  const { isLoading, setLoading } = useFormName();
  const { notificationsContainerRef } = useNotifications();
  const { formatMessage } = useIntl();
  const localizedDateFormat = useLocalizedDateFormat();

  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const toggleForgotPassword = () => setShowForgotPasswordModal(!showForgotPasswordModal);

  const thirdPartySignInConfig = config.features.thirdPartySignIn;
  const customerNumberSignInConfig = config.features.customerNumberSignIn;
  const shouldShowFirstPartySignIn = !thirdPartySignInConfig.disableFirstPartySignIn;
  const shouldShowThirdPartySignIn = thirdPartySignInConfig.enabled && !!thirdPartySignInConfig.providers.length;
  const canSignInWithCustomerNumber = config.features.customerNumberSignIn.enabled;

  const toggleSignInWithCustomerNumber = () =>
    setFieldValue('signInWithCustomerNumber', values.signInWithCustomerNumber === 'active' ? 'inactive' : 'active');

  const onClose = () => {
    toggleForgotPassword();
    notificationsContainerRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  };

  const testIdPrefix = 'login';

  const userHasEmailAddressAndPassword = values.email && values.signInWithCustomerNumber === 'verified';

  const shouldShowInformationBox = values.signInWithCustomerNumber !== 'inactive';

  const getInformationBoxTranslationKey = () => {
    if (values.signInWithCustomerNumber === 'active') {
      return 'auth.options.signIn.informationBox.signInWithCustomerNumber';
    } else if (userHasEmailAddressAndPassword) {
      return 'auth.options.signIn.informationBox.userHasEmailAndPassword';
    } else {
      return 'auth.options.signIn.informationBox.userHasEmailAndNoPassword';
    }
  };

  const handleResetForm = () => {
    setFieldValue('signInWithCustomerNumber', 'inactive');
    setFieldValue('publicKey', '');
    setFieldValue('customerNumberEmail', '');
    setFieldValue('emailHint', '');
  };

  return (
    <div className="option__form">
      <div className="form">
        {shouldShowInformationBox && (
          <div className="form__row">
            <div className="form__field-container">
              <InformationBox text={getInformationBoxTranslationKey()} />
            </div>
          </div>
        )}
        {values.emailHint && (
          <div className="form__row">
            <div className="form__field-container">
              <p className="email-hint">
                <FormattedMessage id="auth.options.signIn.emailHint" values={{ email: values.emailHint }} />
              </p>
            </div>
          </div>
        )}
        {shouldShowFirstPartySignIn && (
          <>
            {showForgotPasswordModal && (
              <Modal onEscapeKey={onClose}>
                <ForgotPassword onClose={onClose} initialEmail={values.email} />
              </Modal>
            )}
            {values.signInWithCustomerNumber !== 'active' ? (
              <>
                <div className="form__row">
                  <div className="form__field-container">
                    {values.signInWithCustomerNumber === 'verified' ? (
                      <Field
                        name="customerNumberEmail"
                        component={TextField}
                        label={formatMessage({ id: 'generic.email' })}
                        maxLength={60}
                        validate={getValidator({ required: true, minLength: 5, format: 'email' })}
                        data-test-id={`${testIdPrefix}-email`}
                        type="email"
                        autoComplete="username"
                        disabled
                      />
                    ) : (
                      <EmailField
                        testIdPrefix={testIdPrefix}
                        autocomplete="username"
                        type="signIn"
                        disabled={!!userHasEmailAddressAndPassword}
                      />
                    )}
                  </div>
                </div>
                <div className="form__row">
                  <div className="form__field-container">
                    <PasswordField
                      testIdPrefix={testIdPrefix}
                      type="signIn"
                      autocomplete="current-password"
                      isInputStatusIconDisabled
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="form__row">
                  <div className="form__field-container">
                    <Field
                      name="publicKey"
                      component={TextField}
                      label={formatMessage({ id: 'auth.options.signIn.customerNumber' })}
                      maxLength={customerNumberSignInConfig.maxLength}
                      validate={getValidator({
                        required: true,
                        format: customerNumberSignInConfig.format
                      })}
                      data-test-id={testIdPrefix}
                    />
                  </div>
                </div>
                <div className="form__row">
                  <div className="form__field-container">
                    <Field
                      name="birthDate"
                      component={DateField}
                      label={`${formatMessage({ id: 'address.birthDate' })} (${localizedDateFormat})`}
                      validate={getBirthDateValidator({ required: true })}
                      data-test-id={testIdPrefix}
                    />
                  </div>
                </div>
              </>
            )}
            {values.signInWithCustomerNumber !== 'active' && (
              <div className="form__row">
                <div className="form__field-container">
                  <span
                    className={classNames('auth-form__forgot-password', { 'cursor-disabled': isLoading })}
                    data-test-id="login-forgot-password"
                    onClick={() => !isLoading && toggleForgotPassword()}
                  >
                    <FormattedMessage id="auth.options.signIn.forgotPassword" />
                  </span>
                </div>
              </div>
            )}
            {canSignInWithCustomerNumber && !['verified', 'setPassword'].includes(values.signInWithCustomerNumber) && (
              <div className="form__row">
                <div className="form__field-container">
                  <span
                    className="auth-form__customer-number"
                    data-test-id="login-with-customer-number"
                    onClick={toggleSignInWithCustomerNumber}
                  >
                    <FormattedMessage
                      id={`auth.options.signIn.${
                        values.signInWithCustomerNumber === 'active' ? 'withEmailAddress' : 'withCustomerNumber'
                      }`}
                    />
                  </span>
                </div>
              </div>
            )}
            <div className="form__row">
              {['setPassword', 'verified'].includes(values.signInWithCustomerNumber) && (
                <div className="form__field-container">
                  <Button
                    type="submit"
                    onClick={handleResetForm}
                    data-test-id="login-reset"
                    secondary
                    disabled={!!isLoading}
                  >
                    <FormattedMessage id="generic.back" />
                  </Button>
                </div>
              )}
              <div className="form__field-container">
                <Button
                  type="submit"
                  onClick={async e => {
                    e.preventDefault();
                    if (isLoading) {
                      return;
                    }
                    if (values.signInWithCustomerNumber === 'active') {
                      setSubmitting(true);
                      setLoading?.(LOADING_TYPES.authPublicKeyAndBirthday);
                      try {
                        const response = await callApi({
                          endpoint: '/customer/publicKeyBirthday',
                          urlParams: { publicKey: values.publicKey, birthDate: values.birthDate },
                          flow: 'legacy'
                        });
                        if (response.authentication?.type !== 'password') {
                          setFieldValue('signInWithCustomerNumber', 'setPassword');
                          setFieldValue('emailHint', response.email);
                        } else {
                          setFieldValue('signInWithCustomerNumber', 'verified');
                          setFieldValue('customerNumberEmail', response.email);
                        }
                        pushTrackingEvent({
                          event: 'login_with_customer_id_and_birthdate',
                          content_name: document.location.pathname,
                          page_type: 'checkout:modal_login',
                          page_type_id: '',
                          method: 'customer_id_and_birthdate',
                          status: 'successful',
                          customer_id: response.id,
                          unique_customer_id: response.publicKey,
                          eh: response.trackingHash,
                          preselected_payment_method: response.selectedPaymentMethod,
                          ...(config.tracking.sendPlaintextEmail && { email: response.email })
                        });
                      } catch (err: any) {
                        if (err.statusCode === 404) {
                          showNotification({
                            level: 'error',
                            text: formatMessage({ id: 'auth.notifications.customerNumberDoesntMatch' }),
                            autoClose: true,
                            closable: true
                          });
                          pushErrorTrackingEventFromI18nKey('auth.notifications.customerNumberDoesntMatch');
                        } else {
                          console.error(err);
                          showDefaultError();
                          pushErrorTrackingEventFromErrorObject(err, 'sign_in_with_customer_number');
                        }
                      }
                      setSubmitting(false);
                      setLoading?.(LOADING_TYPES.off);
                    } else {
                      submitForm();
                    }
                  }}
                  data-test-id="login-submit"
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                  className={classNames({ 'cursor-disabled': !!isLoading })}
                >
                  <FormattedMessage id="auth.options.signIn.label" />
                </Button>
              </div>
            </div>
          </>
        )}
        {shouldShowFirstPartySignIn && shouldShowThirdPartySignIn && (
          <>
            <div
              className={classNames(
                'auth-step-options__divider',
                isWelcomeBack && 'auth-step-options__divider--no-margin'
              )}
            >
              <span className="auth-step-options__divider-line auth-step-options__divider-line--left"></span>
              <FormattedMessage id="generic.or" />
              <span className="auth-step-options__divider-line auth-step-options__divider-line--right"></span>
            </div>
          </>
        )}
        {shouldShowThirdPartySignIn && (
          <ThirdPartySignIn
            isEmbeddedAuth={isEmbeddedAuth}
            providers={thirdPartySignInConfig.providers}
            testIdPrefix={testIdPrefix}
          />
        )}
      </div>
    </div>
  );
};

export default SignIn;
